import { useState } from "react";
import { SubscriptionsClient, type SubscriptionSummary } from "../api/generated";
import { Alert, Chip, Empty, Field, Panel, Stat, useAsync, when } from "../components/ui";

const subscriptions = new SubscriptionsClient("");

/**
 * Console module: <b>Subscriptions</b>.
 *
 * Read-only, and that is the design rather than an omission: a subscription is created with the
 * thing it belongs to — an API key is minted against its (party, channel) binding, a user's inbox
 * is created with the user — because a credential without one cannot answer "what is for me" and a
 * subscription without one cannot be authenticated. Editing them apart is how the two drift.
 *
 * The one number worth watching lives here and nowhere else: a cursor consumer that has stopped
 * committing looks perfectly healthy everywhere. The messages are still there, nothing failed,
 * nothing retried, and the integration has simply not read for a week. The gap between its
 * position and the stream head is the only place that shows.
 */
export function SubscriptionsPage() {
  const { data, error, loading } = useAsync(() => subscriptions.subscriptions_List());
  const [mode, setMode] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const all = data ?? [];
  const rows = all
    .filter((s) => (mode ? s.mode === mode : true))
    .filter((s) => (showInactive ? true : s.isActive));

  const behind = all.filter(isBehind);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Subscriptions</h1>
          <p className="sub">
            Which credential reads as which party, on which channel and in which mode. One party can
            hold several credentials — one per system that reads for it.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      {behind.length > 0 && (
        <Alert kind="warn">
          {behind.length === 1 ? "One cursor consumer is" : `${behind.length} cursor consumers are`}{" "}
          more than 100 messages behind the head of the stream they read. Check that the consuming
          system is still running if the gap does not shrink.
        </Alert>
      )}

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="Bindings" value={all.length} hint="one per credential, channel and stream" />
        <Stat
          label="Inbox mode"
          value={all.filter((s) => s.mode === "inbox").length}
          hint="display clients and people"
        />
        <Stat
          label="Cursor mode"
          value={all.filter((s) => s.mode === "cursor").length}
          hint="systems that process rather than display"
        />
      </div>

      <Panel
        title="Bindings"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <Field label="Mode">
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="">all</option>
                <option value="inbox">inbox</option>
                <option value="cursor">cursor</option>
                <option value="push">push</option>
              </select>
            </Field>
            <label className="checkbox" style={{ alignSelf: "center" }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span>Show revoked</span>
            </label>
          </div>
        }
      >
        {loading ? (
          <Empty>Loading…</Empty>
        ) : rows.length === 0 ? (
          <Empty>
            No bindings. One is created automatically when you issue an API key or add a user.
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Credential</th><th>Party</th><th>Channel</th><th>Mode</th><th>Stream</th>
                <th>Position</th><th>Subject filter</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} style={{ opacity: s.isActive ? 1 : 0.55 }}>
                  <td>
                    {s.principalName}
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {s.principalType}
                      {!s.principalIsActive && " · revoked"}
                    </div>
                  </td>
                  <td>
                    <span className="mono">{s.partyKey ?? "—"}</span>
                    {s.partyName && <div className="muted" style={{ fontSize: 11.5 }}>{s.partyName}</div>}
                  </td>
                  <td><Chip>{s.channel}</Chip></td>
                  <td>{s.mode}{s.isMandatory && <Chip kind="warn">mandatory</Chip>}</td>
                  <td className="mono">{s.streamKey}</td>
                  <td><Position subscription={s} /></td>
                  <td className="mono muted">{s.subjectFilter ?? "—"}</td>
                  <td className="muted">{when(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function Position({ subscription }: { subscription: SubscriptionSummary }) {
  if (subscription.mode !== "cursor") {
    // Inbox mode holds no cursor to lose — the client keeps a watermark and a restart just
    // reloads everything currently for it (§6.4).
    return <span className="muted">no cursor</span>;
  }

  const position = subscription.cursorPosition ?? 0;
  const head = subscription.streamHead ?? 0;
  const gap = head - position;

  return (
    <>
      <span className="mono">{position} / {head}</span>
      {gap > 0 && (
        <div style={{ fontSize: 11.5 }} className={gap > 100 ? "" : "muted"}>
          {gap > 100 ? <span className="chip warn">{gap} behind</span> : `${gap} behind`}
        </div>
      )}
    </>
  );
}

function isBehind(s: SubscriptionSummary): boolean {
  return !!s.isActive
    && s.mode === "cursor"
    && (s.streamHead ?? 0) - (s.cursorPosition ?? 0) > 100;
}
